import { useState } from 'react'

import { ClientError } from '@tribeplatform/gql-client/lib'
import { MassActionRequest } from '@tribeplatform/gql-client/types'
import { T, useI18n, useStaticIntl } from '@tribeplatform/react-components/i18n'
import { useConfirmMassActionRequest } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { FormControl } from '@tribeplatform/react-ui-kit/FormControl'
import { Input } from '@tribeplatform/react-ui-kit/Input'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { MassActionSettingsFactory } from './constants.js'

export interface ConfirmMassActionModalProps {
  open: boolean
  onClose: () => void
  massActionRequest: MassActionRequest
  onSuccess: (m: MassActionRequest) => void
}

export const ConfirmMassActionModal = ({
  open,
  onClose,
  massActionRequest,
  onSuccess,
}: ConfirmMassActionModalProps) => {
  const { $t } = useI18n()
  const [inputValue, setInputValue] = useState<string>('')

  const settings = useStaticIntl(
    MassActionSettingsFactory,
    massActionRequest.context,
    massActionRequest.action,
    massActionRequest.entitiesCount,
  )

  const { mutateAsync: confirmMassActionRequest, isLoading } =
    useConfirmMassActionRequest()

  const { title } = settings
  const buttonEnabled =
    massActionRequest.entitiesCount > 0 &&
    Number(inputValue) === massActionRequest.entitiesCount

  const handleClick = async () => {
    if (!buttonEnabled) {
      return
    }

    try {
      const confirmedMassActionRequest = await confirmMassActionRequest({
        massActionRequestId: massActionRequest.id,
      })
      toast({
        title: $t({
          defaultMessage: 'Action has been initialized',
          id: 'MassAction.SuccessNotice',
        }),
        description:
          settings?.toastMessage ??
          $t({
            defaultMessage: 'This action might take a few minutes to complete.',
            id: 'MassAction.SuccessNotice.Description',
          }),
        status: 'info',
      })
      onSuccess(confirmedMassActionRequest)
      onClose()
    } catch (e) {
      const errors = (e as ClientError)?.response?.errors
      toast({
        title:
          errors?.[0]?.message ??
          $t({
            defaultMessage: 'Unable to perform mass action',
            id: 'MassAction.Failed',
          }),
        status: 'error',
      })
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header title={title} description={settings?.description} />
      <Modal.Content>
        <FormControl.Label>{settings?.countConfirmationMsg}</FormControl.Label>
        <Input
          wrapperClassName="mt-2"
          type="number"
          value={inputValue}
          onChange={e => setInputValue(e.currentTarget.value)}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button
          variant={settings.buttonVariant}
          onClick={handleClick}
          disabled={isLoading || !buttonEnabled}
        >
          {settings?.buttonText}
        </Button>
        <Button
          variant="secondaryNeutral"
          onClick={onClose}
          disabled={isLoading}
        >
          <T defaultMessage="Cancel" id="Generics.Cancel" />
        </Button>
      </Modal.Actions>
    </Modal>
  )
}
